import type {
  FittedComponentDetailPublic,
  MajorAssemblyKind,
} from "@common/client"
import { chapterText } from "@common/lib/ata-chapter"
import { formatCount, formatFlightHours } from "@common/lib/number"
import { z } from "zod"
import type { Display } from "@/components/layout/display-options"
import type { ColumnSizing } from "@/lib/column-widths"
import type { GroupLabel } from "@/lib/grouping"
import {
  type SortValue,
  sortRows,
} from "@/routes/_layout/maintenance-programmes/-lib/sort"
import { UNIT_ABBREVIATIONS } from "@/routes/_layout/maintenance-programmes_/$programmeId/-lib/schedule"
import {
  columnsParam,
  type Direction,
  directionSchema,
  nextSort,
} from "@/routes/_layout/maintenance-programmes_/$programmeId/-lib/table"

export type FittedComponent = FittedComponentDetailPublic

export const FITTED_COMPONENT_PANES: string[] = [
  "/_layout/aircraft_/$aircraftId/fitted-components/new",
]

export const FITTED_COMPONENT_ROUTES: string[] = [
  ...FITTED_COMPONENT_PANES,
  "/_layout/aircraft_/$aircraftId/fitted-components/remove/$componentId",
]

export const MAJOR_ASSEMBLY_KINDS = [
  "engine",
  "apu",
  "propeller_rotor",
  "landing_gear",
] as const satisfies readonly MajorAssemblyKind[]

export function majorAssemblyLabel(kind: MajorAssemblyKind): string {
  switch (kind) {
    case "apu":
      return "APU"
    case "engine":
      return "Engine"
    case "landing_gear":
      return "Landing gear"
    case "propeller_rotor":
      return "Propeller / rotor"
  }
}

export const ASSEMBLIES = [...MAJOR_ASSEMBLY_KINDS, "other"] as const

const assemblySchema = z.enum(ASSEMBLIES)

export type Assembly = z.infer<typeof assemblySchema>

export function assemblyOf(component: FittedComponent): Assembly {
  return component.major_assembly ?? "other"
}

export function assemblyLabel(assembly: Assembly): string {
  return assembly === "other"
    ? "Other components"
    : majorAssemblyLabel(assembly)
}

export const AIRFRAME = "airframe"

export function componentLabel(
  component: Pick<
    FittedComponent,
    "major_assembly" | "part_description" | "part_number" | "position"
  >,
): string {
  const name = component.major_assembly
    ? majorAssemblyLabel(component.major_assembly)
    : (component.part_description ?? component.part_number)
  return [name, component.position].filter(Boolean).join(" ")
}

export function identityText(
  component: Pick<FittedComponent, "part_number" | "serial_number">,
): string {
  return [
    component.part_number,
    component.serial_number && `S/N ${component.serial_number}`,
  ]
    .filter(Boolean)
    .join(" · ")
}

export function installedAtText(component: FittedComponent): string {
  const figures: [number | null | undefined, string][] = [
    [component.installed_hours, UNIT_ABBREVIATIONS.hours],
    [component.installed_cycles, UNIT_ABBREVIATIONS.cycles],
    [component.installed_landings, UNIT_ABBREVIATIONS.landings],
  ]
  return figures
    .flatMap(([value, unit], index) => {
      if (value == null) return []
      const amount = index === 0 ? formatFlightHours(value) : formatCount(value)
      return [`${amount} ${unit}`]
    })
    .join(" · ")
}

export type Components = ReadonlyMap<string, FittedComponent>

export function componentsById(components: FittedComponent[]): Components {
  return new Map(components.map((component) => [component.id, component]))
}

export function parentLabel(
  component: FittedComponent,
  byId: Components,
): string | undefined {
  if (!component.parent_id) return undefined
  const parent = byId.get(component.parent_id)
  return parent ? componentLabel(parent) : "Unlisted component"
}

export function childrenOf(
  component: FittedComponent,
  components: FittedComponent[],
): FittedComponent[] {
  return components.filter((child) => child.parent_id === component.id)
}

export function naturalOrder(components: FittedComponent[]): FittedComponent[] {
  const byName = sortRows(
    components,
    (component) => component.part_description ?? component.part_number,
    "asc",
  )
  return sortRows(byName, assemblyRank, "asc")
}

function assemblyRank(component: FittedComponent): SortValue {
  const kind = component.major_assembly
  if (!kind) return null
  return `${MAJOR_ASSEMBLY_KINDS.indexOf(kind)} ${component.position ?? ""}`
}

export const COMPONENT_COLUMNS = [
  "description",
  "part_number",
  "serial_number",
  "assembly",
  "position",
  "location_number",
  "fitted_to",
  "installed",
  "installed_at",
] as const

const componentColumnSchema = z.enum(COMPONENT_COLUMNS)

export type ComponentColumn = z.infer<typeof componentColumnSchema>

export const FIXED_COMPONENT_COLUMNS: ComponentColumn[] = [
  "description",
  "serial_number",
]

export const COMPONENT_COLUMN_LABELS: Record<ComponentColumn, string> = {
  description: "Description",
  part_number: "Part no.",
  serial_number: "Serial no.",
  assembly: "Assembly",
  position: "Position",
  location_number: "Ref no.",
  fitted_to: "Fitted to",
  installed: "Installed",
  installed_at: "Airframe at install",
}

export const COMPONENT_SIZING: ColumnSizing<ComponentColumn> = {
  id: "aircraft-fitted-components",
  widths: {
    description: 12,
    part_number: 9,
    serial_number: 8,
    assembly: 7.5,
    position: 5.5,
    location_number: 5.5,
    fitted_to: 9,
    installed: 6.5,
    installed_at: 10,
  },
  fill: "description",
  extras: [],
  dropOrder: [
    "installed_at",
    "fitted_to",
    "location_number",
    "position",
    "installed",
    "assembly",
    "part_number",
  ],
}

export const FITTED_VIEWS = ["list", "tree"] as const

const fittedViewSchema = z.enum(FITTED_VIEWS)

export type FittedView = z.infer<typeof fittedViewSchema>

export const COMPONENT_GROUPINGS = [
  "assembly",
  "ata_chapter",
  "fitted_to",
] as const

const componentGroupingSchema = z.enum(COMPONENT_GROUPINGS)

export type ComponentGrouping = z.infer<typeof componentGroupingSchema>

export const COMPONENT_GROUPING_LABELS: Record<ComponentGrouping, string> = {
  assembly: "Assembly",
  ata_chapter: "ATA chapter",
  fitted_to: "Fitted to",
}

export const fittedSearchFields = {
  fitted_view: fittedViewSchema.optional().catch(undefined),
  fitted_search: z.string().optional().catch(undefined),
  fitted_assembly: z.array(assemblySchema).optional().catch(undefined),
  fitted_parent: z.array(z.string()).optional().catch(undefined),
  fitted_ata: z.array(z.string()).optional().catch(undefined),
  fitted_group: z
    .enum([...COMPONENT_GROUPINGS, "none"])
    .optional()
    .catch(undefined),
  fitted_subgroup: componentGroupingSchema.optional().catch(undefined),
  fitted_columns: z.array(componentColumnSchema).optional().catch(undefined),
  fitted_sort: componentColumnSchema.optional().catch(undefined),
  fitted_direction: directionSchema.optional().catch(undefined),
  fitted_item: z.string().optional().catch(undefined),
}

const fittedSearchSchema = z.object(fittedSearchFields)

export type FittedSearch = z.infer<typeof fittedSearchSchema>

export type FittedFilterValues = Pick<
  FittedSearch,
  "fitted_assembly" | "fitted_parent" | "fitted_ata"
>

export const NO_FITTED_FILTERS: FittedFilterValues = {
  fitted_assembly: undefined,
  fitted_parent: undefined,
  fitted_ata: undefined,
}

export function hasFittedFilters(values: FittedFilterValues): boolean {
  return Boolean(
    values.fitted_assembly?.length ||
      values.fitted_parent?.length ||
      values.fitted_ata?.length,
  )
}

export type FittedDisplay = Display<ComponentGrouping> & {
  view: FittedView
  columns: ComponentColumn[]
  sort: ComponentColumn
  direction: Direction
}

const DEFAULT_SORT: ComponentColumn = "assembly"

export const TREE_COMPONENT_COLUMNS = COMPONENT_COLUMNS.filter(
  (column) => column !== "fitted_to",
)

export function fittedDisplayOf(search: FittedSearch): FittedDisplay {
  const view = search.fitted_view ?? "list"
  const group =
    search.fitted_group === "none"
      ? undefined
      : (search.fitted_group ?? "assembly")
  const picked = search.fitted_columns
  const options: readonly ComponentColumn[] =
    view === "tree" ? TREE_COMPONENT_COLUMNS : COMPONENT_COLUMNS
  const sort = search.fitted_sort ?? DEFAULT_SORT
  return {
    view,
    group,
    subgroup:
      group && search.fitted_subgroup !== group
        ? search.fitted_subgroup
        : undefined,
    columns: options.filter(
      (column) =>
        !picked ||
        FIXED_COMPONENT_COLUMNS.includes(column) ||
        picked.includes(column),
    ),
    sort: options.includes(sort) ? sort : DEFAULT_SORT,
    direction: search.fitted_direction ?? "asc",
  }
}

export function fittedViewSearch(view: FittedView): Partial<FittedSearch> {
  return { fitted_view: view === "list" ? undefined : view }
}

export function fittedGroupingSearch(
  next: Display<ComponentGrouping>,
): Partial<FittedSearch> {
  const group = next.group ?? "none"
  return {
    fitted_group: group === "assembly" ? undefined : group,
    fitted_subgroup: next.subgroup,
  }
}

export function fittedSortSearch(
  display: FittedDisplay,
  column: ComponentColumn,
): Partial<FittedSearch> {
  const { sort, direction } = nextSort(display, column, DEFAULT_SORT)
  return { fitted_sort: sort, fitted_direction: direction }
}

export function fittedColumnsSearch(
  search: FittedSearch,
  columns: ComponentColumn[],
): Partial<FittedSearch> {
  const listed = search.fitted_columns ?? COMPONENT_COLUMNS
  const keepsFittedTo =
    search.fitted_view === "tree" && listed.includes("fitted_to")
  const next: ComponentColumn[] = keepsFittedTo
    ? [...columns, "fitted_to"]
    : columns
  return { fitted_columns: columnsParam(next, COMPONENT_COLUMNS) }
}

function sortValue(
  component: FittedComponent,
  column: ComponentColumn,
  byId: Components,
): SortValue {
  switch (column) {
    case "description":
      return component.part_description ?? component.part_number
    case "part_number":
      return component.part_number
    case "serial_number":
      return component.serial_number ?? null
    case "assembly":
      return assemblyRank(component)
    case "position":
      return component.position ?? null
    case "location_number":
      return component.location_number ?? null
    case "fitted_to":
      return parentLabel(component, byId) ?? null
    case "installed":
      return component.installed_date ?? null
    case "installed_at":
      return component.installed_hours ?? null
  }
}

function matchesText(
  component: FittedComponent,
  text: string,
  byId: Components,
): boolean {
  const needle = text.toLowerCase()
  return [
    component.part_description,
    component.part_number,
    component.serial_number,
    component.position,
    component.location_number,
    component.major_assembly && majorAssemblyLabel(component.major_assembly),
    component.part_instance?.part.manufacturer_name,
    parentLabel(component, byId),
  ].some((value) => value?.toLowerCase().includes(needle))
}

export function parentKey(component: FittedComponent): string {
  return component.parent_id ?? AIRFRAME
}

export function chapterKey(component: FittedComponent): string {
  return component.ata_chapter === null ? "" : String(component.ata_chapter)
}

function matchesFilters(
  component: FittedComponent,
  values: FittedFilterValues,
): boolean {
  const within = (picked: string[] | undefined, value: string) =>
    !picked?.length || picked.includes(value)
  return (
    within(values.fitted_assembly, assemblyOf(component)) &&
    within(values.fitted_parent, parentKey(component)) &&
    within(values.fitted_ata, chapterKey(component))
  )
}

function matchesSearch(
  component: FittedComponent,
  search: FittedSearch,
  byId: Components,
): boolean {
  const text = search.fitted_search
  return (
    (!text || matchesText(component, text, byId)) &&
    matchesFilters(component, search)
  )
}

function sortedComponents(
  components: FittedComponent[],
  display: FittedDisplay,
  byId: Components,
): FittedComponent[] {
  return sortRows(
    naturalOrder(components),
    (component) => sortValue(component, display.sort, byId),
    display.direction,
  )
}

export function shownComponents(
  components: FittedComponent[],
  search: FittedSearch,
  display: FittedDisplay,
  byId: Components,
): FittedComponent[] {
  return sortedComponents(components, display, byId).filter((component) =>
    matchesSearch(component, search, byId),
  )
}

export type TreeRow = {
  component: FittedComponent
  depth: number
  rails: boolean[]
  last: boolean
  branch: boolean
  matched: boolean
}

function treeParent(component: FittedComponent, byId: Components): string {
  return component.parent_id && byId.has(component.parent_id)
    ? component.parent_id
    : AIRFRAME
}

function lineage(
  component: FittedComponent,
  byId: Components,
  seen: string[] = [],
): string[] {
  const path = [...seen, component.id]
  const parent = component.parent_id ? byId.get(component.parent_id) : undefined
  return parent && !path.includes(parent.id)
    ? lineage(parent, byId, path)
    : path
}

export function componentTree(
  components: FittedComponent[],
  search: FittedSearch,
  display: FittedDisplay,
  byId: Components,
  collapsed: ReadonlySet<string>,
): TreeRow[] {
  const sorted = sortedComponents(components, display, byId)
  const matched = new Set(
    sorted
      .filter((component) => matchesSearch(component, search, byId))
      .map((component) => component.id),
  )
  const kept = new Set(
    sorted
      .filter((component) => matched.has(component.id))
      .flatMap((component) => lineage(component, byId)),
  )
  return treeRows({
    children: childrenByParent(
      sorted.filter(({ id }) => kept.has(id)),
      byId,
    ),
    from: AIRFRAME,
    depth: 1,
    collapsed,
    matched: (component) => matched.has(component.id),
  })
}

export function subtreeRows(
  component: FittedComponent,
  components: FittedComponent[],
  byId: Components,
  collapsed: ReadonlySet<string>,
): TreeRow[] {
  return treeRows({
    children: childrenByParent(naturalOrder(components), byId),
    from: component.id,
    depth: 0,
    collapsed,
    matched: () => true,
  })
}

export const NOTHING_COLLAPSED: ReadonlySet<string> = new Set()

export function treeSearchKey(search: FittedSearch): string {
  return JSON.stringify([
    search.fitted_search,
    search.fitted_assembly,
    search.fitted_parent,
    search.fitted_ata,
  ])
}

export function treeOrder(
  rows: TreeRow[],
  collapsed: ReadonlySet<string>,
): FittedComponent[] {
  return collapsed.has(AIRFRAME)
    ? []
    : rows.filter((row) => row.matched).map((row) => row.component)
}

function childrenByParent(
  components: FittedComponent[],
  byId: Components,
): ReadonlyMap<string, FittedComponent[]> {
  const children = new Map<string, FittedComponent[]>()
  for (const component of components) {
    const parent = treeParent(component, byId)
    const siblings = children.get(parent)
    if (siblings) siblings.push(component)
    else children.set(parent, [component])
  }
  return children
}

function treeRows({
  children,
  from,
  depth,
  collapsed,
  matched,
}: {
  children: ReadonlyMap<string, FittedComponent[]>
  from: string
  depth: number
  collapsed: ReadonlySet<string>
  matched: (component: FittedComponent) => boolean
}): TreeRow[] {
  const walk = (parent: string, level: number, rails: boolean[]): TreeRow[] => {
    const siblings = children.get(parent) ?? []
    return siblings.flatMap((component, index) => {
      const last = index === siblings.length - 1
      const row = {
        component,
        depth: level,
        rails,
        last,
        branch: children.has(component.id),
        matched: matched(component),
      }
      const below = level === 0 ? [] : [...rails, !last]
      return collapsed.has(component.id)
        ? [row]
        : [row, ...walk(component.id, level + 1, below)]
    })
  }

  return walk(from, depth, [])
}

export type ComponentGroupOf = (
  component: FittedComponent,
  grouping: ComponentGrouping,
) => GroupLabel

const NO_CHAPTER_GROUP: GroupLabel = {
  key: "",
  label: "No ATA chapter",
  order: 1000,
}

export function groupOfComponents(
  byId: Components,
  chapters: ReadonlyMap<number, string>,
): ComponentGroupOf {
  return (component, grouping) => {
    switch (grouping) {
      case "assembly": {
        const assembly = assemblyOf(component)
        return {
          key: assembly,
          label: assemblyLabel(assembly),
          order: ASSEMBLIES.indexOf(assembly),
        }
      }
      case "ata_chapter":
        return component.ata_chapter === null
          ? NO_CHAPTER_GROUP
          : {
              key: chapterKey(component),
              label: `ATA ${chapterText(component.ata_chapter)}`,
              detail: chapters.get(component.ata_chapter),
              order: component.ata_chapter,
            }
      case "fitted_to": {
        if (!component.parent_id) {
          return { key: AIRFRAME, label: "Airframe", order: 0 }
        }
        const parent = byId.get(component.parent_id)
        return {
          key: component.parent_id,
          label: parent ? componentLabel(parent) : "Unlisted component",
          detail: parent ? identityText(parent) : undefined,
          order: 1,
        }
      }
    }
  }
}
